/**
 * Подсказки по технике — то, что открывается кнопкой «?» рядом с движением.
 *
 * ПОЧЕМУ ТЕКСТ ЛЕЖИТ В СБОРКЕ, А КАРТИНКИ — НЕТ. Подсказка нужна ровно там, где
 * связи обычно нет: в зале, между подходами, когда человек впервые видит
 * незнакомое движение. Текст весит несколько килобайт и едет вместе с
 * приложением, поэтому работает всегда. Картинки лежат отдельными файлами в
 * `/technique/` и грузятся только при открытии карточки: тащить их в сборку
 * значило бы платить мегабайтом при каждом запуске ради экрана, который
 * открывают раз в неделю.
 *
 * ОТКУДА КАРТИНКИ. Набор Everkinetic с Викисклада, лицензия CC BY-SA 3.0.
 * Атрибуция обязательна и показывается в карточке — не мелким шрифтом «для
 * галочки», а ссылкой на источник каждого рисунка.
 *
 * ЧЕГО ЗДЕСЬ НЕТ. Ни одного медицинского утверждения (раздел 14): подсказки
 * описывают, КАК выполняется движение, и не обещают, что от этого что-то не
 * заболит и что-то вылечится. Где картинка изображает вариант движения, а не
 * ровно наше, это написано в `note` — подпись честнее молчания, а неверная
 * картинка хуже её отсутствия, потому что по ней будут делать.
 *
 * Личные движения человека подсказок не имеют: их придумал он сам, и
 * сочинять технику за него приложение не вправе.
 */

export interface Technique {
  /** Как встать, лечь, взяться — до первого повторения. */
  setup: string[]
  /** Само движение. */
  execution: string[]
  /** Частые ошибки: то, что стоит проверить у себя. */
  mistakes: string[]
}

/** Кадры рисунка: начало и конец движения. */
export interface TechniqueImages {
  frames: string[]
  /** Чем рисунок отличается от нашего движения. Пусто — ничем. */
  note: string
  /** Страницы файлов на Викискладе — для атрибуции. */
  sources: string[]
}

export const TECHNIQUE_LICENSE = {
  author: 'Everkinetic',
  license: 'CC BY-SA 3.0',
  licenseUrl: 'https://creativecommons.org/licenses/by-sa/3.0',
}

export const TECHNIQUE: Record<number, Technique> = {
  1: {
    setup: [
      'Лопатки сведены и прижаты к скамье, грудь раскрыта.',
      'Стопы плотно в полу, таз на скамье.',
      'Хват чуть шире плеч, гриф лежит в основании ладони.',
    ],
    execution: [
      'Опускай гриф к низу груди, локти примерно под 45° к корпусу.',
      'Касание груди контролируемое, без отбива.',
      'Жми вверх и чуть назад, к стойкам.',
    ],
    mistakes: [
      'Локти разведены в линию плеч.',
      'Таз отрывается от скамьи ради лишнего повтора.',
    ],
  },
  2: {
    setup: [
      'Наклон скамьи около 30°.',
      'Лопатки сведены, стопы в полу.',
      'Хват чуть шире плеч.',
    ],
    execution: [
      'Опускай гриф к верху груди, под ключицы.',
      'Локти чуть прижаты, не разводятся в линию.',
      'Жми вертикально вверх.',
    ],
    mistakes: [
      'Наклон 45° и выше — работа уходит в передние дельты.',
      'Гриф опускается на живот.',
    ],
  },
  3: {
    setup: [
      'Гантели на бёдрах, ложись вместе с ними, закидывая коленями.',
      'Лопатки сведены, стопы в полу.',
      'Гантели над грудью, ладони смотрят вперёд.',
    ],
    execution: [
      'Опускай до уровня груди, до растяжения.',
      'Локти примерно под 45°.',
      'Вверху не стучи гантелями друг о друга.',
    ],
    mistakes: [
      'Погоня за амплитудой глубже комфортной.',
      'Гантели уезжают к голове.',
    ],
  },
  4: {
    setup: [
      'Наклон скамьи около 30°.',
      'Гантели закидываются коленями, лопатки сведены.',
      'Стопы в полу.',
    ],
    execution: [
      'Опускай к верху груди.',
      'Локти чуть внутрь, не в линию плеч.',
      'Вверх по дуге, к центру.',
    ],
    mistakes: ['Слишком большой наклон скамьи.', 'Плечи проваливаются вперёд.'],
  },
  5: {
    setup: [
      'Сиденье так, чтобы рукояти были на уровне середины груди.',
      'Спина и лопатки прижаты к спинке.',
      'Стопы в полу.',
    ],
    execution: [
      'Жми плавно, без рывка со старта.',
      'Локти под 45°, не в линию плеч.',
      'Не выпрямляй локти в замок.',
    ],
    mistakes: ['Слишком высокое или низкое сиденье.', 'Отрыв лопаток от спинки.'],
  },
  6: {
    setup: [
      'Руки прямые на брусьях, плечи опущены от ушей.',
      'Корпус слегка наклонён вперёд — так работа идёт в грудь.',
      'Ноги согнуты и скрещены.',
    ],
    execution: [
      'Опускайся примерно до прямого угла в локте.',
      'Локти назад, не в стороны.',
      'Выжимай без рывка, не выпрямляя локти в замок.',
    ],
    mistakes: [
      'Строго вертикальный корпус — это уже вариант на трицепс.',
      'Провал глубже управляемой амплитуды.',
    ],
  },
  7: {
    setup: [
      'Ладони чуть шире плеч, под грудью.',
      'Тело — прямая линия от пяток до головы.',
      'Пресс и ягодицы в тонусе.',
    ],
    execution: [
      'Опускайся, пока грудь не окажется в кулаке от пола.',
      'Локти под 45°, не в стороны.',
      'Выжимай, сохраняя линию тела.',
    ],
    mistakes: ['Таз провисает или задран вверх.', 'Голова тянется вперёд.'],
  },
  10: {
    setup: [
      'Гриф на передних дельтах, хват чуть шире плеч.',
      'Стопы на ширине таза.',
      'Рёбра не задраны, пресс и ягодицы в тонусе.',
    ],
    execution: [
      'Убери голову назад, чтобы гриф прошёл по прямой.',
      'Жми вертикально, в конце подведи голову под гриф.',
      'Опускай по той же прямой.',
    ],
    mistakes: [
      'Отклонение корпуса назад вместо работы плеч.',
      'Гриф идёт по дуге вперёд.',
    ],
  },
  11: {
    setup: [
      'Спинка почти вертикально.',
      'Гантели у плеч, ладони вперёд.',
      'Стопы в полу, спина прижата.',
    ],
    execution: [
      'Жми вверх и чуть внутрь.',
      'Локти не заваливай далеко назад.',
      'Опускай до уровня ушей.',
    ],
    mistakes: ['Слишком глубокое опускание.', 'Отбив поясницей от спинки.'],
  },
  12: {
    setup: [
      'Сиденье так, чтобы рукояти были на уровне плеч.',
      'Спина прижата к спинке.',
      'Стопы в полу.',
    ],
    execution: [
      'Жми плавно, без рывка со старта.',
      'Не выпрямляй локти в замок.',
      'Возвращай по той же траектории.',
    ],
    mistakes: ['Слишком низкое сиденье — старт из-за головы.', 'Рывок корпусом.'],
  },
  20: {
    setup: [
      'Гриф над серединой стопы, хват чуть шире плеч.',
      'Наклон корпуса около 45°, спина прямая.',
      'Колени чуть согнуты.',
    ],
    execution: [
      'Тяни к низу живота, локти вдоль корпуса.',
      'В верхней точке сведи лопатки.',
      'Опускай под контролем, не бросай.',
    ],
    mistakes: [
      'Корпус выпрямляется по ходу подхода — вес идёт рывком.',
      'Круглая спина.',
    ],
  },
  21: {
    setup: [
      'Колено и одноимённая рука на скамье.',
      'Спина параллельна полу и прямая.',
      'Гантель в свободной руке, рука прямая.',
    ],
    execution: [
      'Тяни локтем назад, вдоль корпуса.',
      'В верхней точке — лопатка к позвоночнику.',
      'Опускай до полного растяжения.',
    ],
    mistakes: [
      'Корпус проворачивается вслед за рукой.',
      'Тяга бицепсом вместо спины.',
    ],
  },
  22: {
    setup: [
      'Стопы в упоры, колени чуть согнуты.',
      'Корпус вертикально, спина прямая.',
      'Возьмись за рукоять, руки прямые.',
    ],
    execution: [
      'Тяни к низу живота, сводя лопатки.',
      'Локти вдоль корпуса.',
      'Возвращай, отпуская лопатки вперёд, но не кругля спину.',
    ],
    mistakes: ['Раскачка корпусом вперёд-назад.', 'Плечи задираются к ушам.'],
  },
  23: {
    setup: [
      'Отрегулируй упор в грудь и высоту сиденья.',
      'Грудь в упоре, спина прямая.',
      'Возьмись за рукояти, руки прямые.',
    ],
    execution: [
      'Тяни локтями назад, сводя лопатки.',
      'Не отрывай грудь от упора.',
      'Возвращай под контролем.',
    ],
    mistakes: ['Отрыв от упора ради лишнего веса.', 'Работа только руками.'],
  },
  30: {
    setup: [
      'Хват чуть шире плеч, ладони от себя.',
      'Плечи опущены от ушей, лопатки собраны.',
      'Ноги можно скрестить.',
    ],
    execution: [
      'Тяни грудью к перекладине, локти вниз и назад.',
      'Вверху грудь ближе к перекладине, а не подбородок вперёд.',
      'Опускайся до почти прямых рук, под контролем.',
    ],
    mistakes: ['Раскачка и подброс тазом.', 'Опускание в свободное падение.'],
  },
  31: {
    setup: [
      'Хват на ширине плеч, ладони к себе.',
      'Плечи опущены, лопатки собраны.',
      'Корпус без раскачки.',
    ],
    execution: [
      'Тяни, ведя локти вниз вдоль корпуса.',
      'Вверху грудь к перекладине.',
      'Опускайся под контролем.',
    ],
    mistakes: ['Слишком узкий хват.', 'Рывок корпусом.'],
  },
  32: {
    setup: [
      'Валики плотно на бёдрах.',
      'Хват шире плеч.',
      'Корпус слегка отклонён назад, грудь раскрыта.',
    ],
    execution: [
      'Тяни рукоять к верху груди, локти вниз.',
      'Сводя лопатки в нижней точке.',
      'Возвращай вверх до полного растяжения.',
    ],
    mistakes: ['Тяга за голову.', 'Отклонение корпуса назад рывком.'],
  },
  40: {
    setup: [
      'Гриф на трапециях, лопатки сведены.',
      'Стопы на ширине плеч, носки чуть в стороны.',
      'Спина прямая, взгляд вперёд.',
    ],
    execution: [
      'Начинай с отведения таза назад, одновременно сгибая колени.',
      'Опускайся минимум до параллели бедра с полом.',
      'Колени идут в направлении носков.',
    ],
    mistakes: ['Пятки отрываются от пола.', 'Спина круглится в нижней точке.'],
  },
  41: {
    setup: [
      'Гриф на передних дельтах, локти высоко и смотрят вперёд.',
      'Стопы на ширине плеч.',
      'Корпус вертикальнее, чем в обычном приседе.',
    ],
    execution: [
      'Опускайся вертикально, колени вперёд.',
      'Держи локти высоко весь подход.',
      'До параллели или ниже, если позволяет амплитуда.',
    ],
    mistakes: [
      'Локти опускаются — гриф скатывается вперёд.',
      'Слишком узкая постановка стоп.',
    ],
  },
  42: {
    setup: [
      'Спина и таз прижаты к спинке.',
      'Стопы на платформе на ширине плеч, по центру.',
      'Сними платформу со стопоров.',
    ],
    execution: [
      'Опускай примерно до прямого угла в колене.',
      'Таз не отрывается от спинки.',
      'Выжимай, не выпрямляя колени в замок.',
    ],
    mistakes: [
      'Слишком глубокое опускание — таз подворачивается.',
      'Колени заваливаются внутрь.',
    ],
  },
  43: {
    setup: [
      'Задняя нога подъёмом стопы на скамье.',
      'Передняя нога в шаге вперёд.',
      'Гантели в руках, корпус чуть наклонён.',
    ],
    execution: [
      'Опускайся вертикально вниз, до параллели бедра передней ноги.',
      'Вес на всей стопе передней ноги.',
      'Выжимай передней ногой.',
    ],
    mistakes: ['Слишком короткий шаг вперёд.', 'Заваливание набок.'],
  },
  50: {
    setup: [
      'Гриф над серединой стопы, почти касается голени.',
      'Стопы на ширине таза, хват чуть шире ног.',
      'Спина прямая, плечи чуть впереди грифа.',
    ],
    execution: [
      'Начни с продавливания пола ногами.',
      'Гриф идёт вплотную к ноге, таз и плечи поднимаются вместе.',
      'Выпрямись, не отклоняясь назад.',
    ],
    mistakes: [
      'Таз стартует раньше плеч — тяга превращается в наклон.',
      'Гриф уходит от ног вперёд.',
    ],
  },
  51: {
    setup: [
      'Штанга в руках, стоя.',
      'Колени чуть согнуты и остаются так весь подход.',
      'Спина прямая, лопатки сведены.',
    ],
    execution: [
      'Веди таз назад, опуская гриф вдоль ног.',
      'Опускайся до растяжения задней поверхности бедра.',
      'Возвращайся, подавая таз вперёд.',
    ],
    mistakes: [
      'Сгибание коленей вместо движения тазом — получается становая.',
      'Круглая спина в нижней точке.',
    ],
  },
  52: {
    setup: [
      'Лопатки на скамье, штанга на складке таза через накладку.',
      'Стопы на ширине таза.',
      'Голень вертикально в верхней точке.',
    ],
    execution: [
      'Толкай пятками, поднимая таз до линии плечи–таз–колени.',
      'Вверху сожми ягодицы.',
      'Опускай под контролем.',
    ],
    mistakes: [
      'Прогиб в пояснице вместо работы ягодиц.',
      'Стопы слишком далеко — работает задняя поверхность бедра.',
    ],
  },
  53: {
    setup: [
      'Валики под бёдрами, край упора чуть ниже таза.',
      'Стопы зафиксированы.',
      'Тело — прямая линия.',
    ],
    execution: [
      'Опускайся, сгибаясь в тазу, а не в пояснице.',
      'Поднимайся до линии тела, не выше.',
      'Движение плавное, без рывка.',
    ],
    mistakes: ['Переразгибание в верхней точке.', 'Рывок с раскачкой.'],
  },
  60: {
    setup: [
      'Стоя, гантели по бокам, локти чуть согнуты.',
      'Корпус слегка наклонён вперёд.',
      'Плечи опущены.',
    ],
    execution: [
      'Поднимай в стороны до уровня плеч.',
      'Веди локтем, а не кистью.',
      'Опускай медленнее, чем поднимаешь.',
    ],
    mistakes: ['Заброс весом с раскачкой.', 'Подъём выше плеч с задиранием трапеций.'],
  },
  61: {
    setup: [
      'Блок на уровне лица, канатная рукоять.',
      'Шаг назад, руки прямые.',
      'Корпус вертикально.',
    ],
    execution: [
      'Тяни к лицу, разводя руки в стороны.',
      'Локти выше кистей.',
      'В конце разверни кисти наружу.',
    ],
    mistakes: ['Тяга к груди вместо лица.', 'Вес такой, что тянет корпус.'],
  },
  62: {
    setup: [
      'Стоя, гантели в опущенных руках.',
      'Локти прижаты к корпусу.',
      'Спина прямая.',
    ],
    execution: [
      'Сгибай руку, не двигая локтем.',
      'Вверху короткая пауза.',
      'Опускай под контролем, до полного выпрямления.',
    ],
    mistakes: ['Локти уезжают вперёд.', 'Раскачка корпусом.'],
  },
  63: {
    setup: [
      'Нижний блок, прямая или изогнутая рукоять.',
      'Шаг назад, чтобы трос был натянут.',
      'Локти у корпуса.',
    ],
    execution: [
      'Сгибай, удерживая локти на месте.',
      'Вверху не доводи до потери натяжения.',
      'Опускай медленно — блок тянет обратно.',
    ],
    mistakes: ['Отклонение корпуса назад.', 'Локти уходят вперёд.'],
  },
  64: {
    setup: [
      'Верхний блок, рукоять на уровне груди.',
      'Локти прижаты к корпусу.',
      'Небольшой наклон вперёд.',
    ],
    execution: [
      'Разгибай, двигая только предплечьем.',
      'Внизу полное выпрямление и короткая пауза.',
      'Возвращай примерно до прямого угла.',
    ],
    mistakes: ['Локти уходят в стороны или вперёд.', 'Помощь корпусом.'],
  },
  65: {
    setup: [
      'Лёжа на скамье, гантели над грудью, ладони друг к другу.',
      'Локти направлены вверх.',
      'Лопатки сведены, стопы в полу.',
    ],
    execution: [
      'Опускай гантели к вискам, сгибая только локоть.',
      'Плечо остаётся неподвижным.',
      'Разгибай, не выпрямляя локти в замок.',
    ],
    mistakes: [
      'Локти разъезжаются в стороны.',
      'Плечо уходит назад — движение становится пуловером.',
    ],
  },
  66: {
    setup: [
      'Сиденье так, чтобы рукояти были на уровне груди.',
      'Спина прижата к спинке.',
      'Локти чуть согнуты и зафиксированы.',
    ],
    execution: [
      'Сводя руки, веди их по дуге.',
      'В конце короткая пауза.',
      'Разводи медленно, до растяжения без дискомфорта.',
    ],
    mistakes: [
      'Сгибание-разгибание локтя вместо сведения.',
      'Слишком большая амплитуда назад.',
    ],
  },
  67: {
    setup: [
      'Спина прижата к спинке, колени на оси вращения.',
      'Валик на подъёме стопы, не на голени.',
      'Возьмись за рукояти сиденья.',
    ],
    execution: [
      'Разгибай до почти прямой ноги.',
      'Вверху короткая пауза.',
      'Опускай под контролем.',
    ],
    mistakes: ['Рывок и отрыв таза от сиденья.', 'Опускание в свободное падение.'],
  },
  68: {
    setup: [
      'Валик над пяткой, колени на оси вращения.',
      'Таз прижат.',
      'Возьмись за рукояти.',
    ],
    execution: [
      'Сгибай до конца доступной амплитуды.',
      'Короткая пауза.',
      'Возвращай медленно, без рывка в конце.',
    ],
    mistakes: ['Отрыв таза ради лишнего веса.', 'Слишком быстрое возвращение.'],
  },
  69: {
    setup: [
      'Плечи под упорами, носки на платформе, пятки на весу.',
      'Колени прямые, но не в замке.',
      'Корпус вертикально.',
    ],
    execution: [
      'Опусти пятки до растяжения.',
      'Поднимись максимально высоко на носки.',
      'Вверху пауза.',
    ],
    mistakes: ['Короткая пружинистая амплитуда.', 'Сгибание коленей.'],
  },
  80: {
    setup: [
      'Упор на предплечья, локти под плечами.',
      'Тело — прямая линия от пяток до головы.',
      'Пресс и ягодицы в тонусе.',
    ],
    execution: [
      'Держи таз на линии тела.',
      'Дыши ровно, не задерживай дыхание.',
      'Заканчивай, когда линия начинает ломаться.',
    ],
    mistakes: ['Таз провисает или задран.', 'Голова опущена или задрана.'],
  },
  81: {
    setup: [
      'Вис на перекладине, плечи опущены от ушей.',
      'Корпус без раскачки.',
      'Хват на ширине плеч.',
    ],
    execution: [
      'Поднимай ноги, подкручивая таз, а не только сгибая бедро.',
      'Опускай медленно.',
      'Прямые ноги не идут — согни колени.',
    ],
    mistakes: ['Раскачка и заброс инерцией.', 'Подъём одним бедром без подкрутки таза.'],
  },
  82: {
    setup: [
      'На коленях у блока, канат у головы.',
      'Таз зафиксирован.',
      'Локти согнуты и остаются так.',
    ],
    execution: [
      'Скручивайся, приближая рёбра к тазу.',
      'Работает пресс, а не руки.',
      'Возвращайся под контролем.',
    ],
    mistakes: ['Движение тазом вместо скручивания.', 'Тяга руками вниз.'],
  },
}

/**
 * Рисунки: какие кадры есть и чем они отличаются от нашего движения.
 *
 * Собрано скриптом из манифеста рядом с самими файлами — руками этот
 * список не правят: разъехавшись с содержимым `/technique/`, он даст
 * битую картинку вместо подсказки.
 */
export const TECHNIQUE_IMAGES: Record<number, TechniqueImages> = {
  1: {
    frames: ['1-1.png', '1-2.png'],
    note: '',
    sources: [
      'https://commons.wikimedia.org/wiki/File:Bench_press_1.svg',
      'https://commons.wikimedia.org/wiki/File:Bench_press_2.svg',
    ],
  },
  2: {
    frames: ['2-1.png', '2-2.png'],
    note: '',
    sources: [
      'https://commons.wikimedia.org/wiki/File:Incline_bench_press_1.svg',
      'https://commons.wikimedia.org/wiki/File:Incline_bench_press_2.svg',
    ],
  },
  3: {
    frames: ['3-1.png', '3-2.png'],
    note: '',
    sources: [
      'https://commons.wikimedia.org/wiki/File:Bench_press_dumbbell_1.svg',
      'https://commons.wikimedia.org/wiki/File:Bench_press_dumbbell_2.svg',
    ],
  },
  4: {
    frames: ['4-1.png', '4-2.png'],
    note: '',
    sources: [
      'https://commons.wikimedia.org/wiki/File:Dumbbell_incline_bench_press_1.svg',
      'https://commons.wikimedia.org/wiki/File:Dumbbell_incline_bench_press_2.svg',
    ],
  },
  5: {
    frames: ['5-1.png', '5-2.png'],
    note: '',
    sources: [
      'https://commons.wikimedia.org/wiki/File:Machine_bench_press_1.svg',
      'https://commons.wikimedia.org/wiki/File:Machine_bench_press_2.svg',
    ],
  },
  6: {
    frames: ['6-1.png', '6-2.png'],
    note: '',
    sources: [
      'https://commons.wikimedia.org/wiki/File:Chest_dips_1.svg',
      'https://commons.wikimedia.org/wiki/File:Chest_dips_2.svg',
    ],
  },
  7: {
    frames: ['7-1.png', '7-2.png'],
    note: '',
    sources: [
      'https://commons.wikimedia.org/wiki/File:Push_ups_close_and_wide_hand_versions_1.svg',
      'https://commons.wikimedia.org/wiki/File:Push_ups_close_and_wide_hand_versions_2.svg',
    ],
  },
  10: {
    frames: ['10-1.png', '10-2.png'],
    note: '',
    sources: [
      'https://commons.wikimedia.org/wiki/File:Barbell_shoulder_press_1.svg',
      'https://commons.wikimedia.org/wiki/File:Barbell_shoulder_press_2.svg',
    ],
  },
  11: {
    frames: ['11-1.png', '11-2.png'],
    note: '',
    sources: [
      'https://commons.wikimedia.org/wiki/File:Dumbbell_shoulder_press_1.svg',
      'https://commons.wikimedia.org/wiki/File:Dumbbell_shoulder_press_2.svg',
    ],
  },
  12: {
    frames: ['12-1.png', '12-2.png'],
    note: '',
    sources: [
      'https://commons.wikimedia.org/wiki/File:Seated_shoulder_press_machine_1.svg',
      'https://commons.wikimedia.org/wiki/File:Seated_shoulder_press_machine_2.svg',
    ],
  },
  20: {
    frames: ['20-1.png', '20-2.png'],
    note: 'На рисунке обратный хват. Движение то же, наклон и траектория те же — в нашем варианте хват прямой.',
    sources: [
      'https://commons.wikimedia.org/wiki/File:Reverse_grips_bent_over_barbell_rows_1.svg',
      'https://commons.wikimedia.org/wiki/File:Reverse_grips_bent_over_barbell_rows_2.svg',
    ],
  },
  22: {
    frames: ['22-1.png', '22-2.png'],
    note: '',
    sources: [
      'https://commons.wikimedia.org/wiki/File:Seated_cable_rows_1.svg',
      'https://commons.wikimedia.org/wiki/File:Seated_cable_rows_2.svg',
    ],
  },
  30: {
    frames: ['30-pullup-1.png', '30-pullup-2.png'],
    note: '',
    sources: [
      'https://commons.wikimedia.org/wiki/File:Pull_ups_1.svg',
      'https://commons.wikimedia.org/wiki/File:Pull_ups_2.svg',
    ],
  },
  31: {
    frames: ['31-1.png', '31-2.png'],
    note: 'Рисунок — вид сзади: направление ладоней на нём не разглядеть, а руки стоят уже, чем нужно. Хват обратный — ладони смотрят на тебя, руки на ширине плеч.',
    sources: [
      'https://commons.wikimedia.org/wiki/File:Chin_ups_1.svg',
      'https://commons.wikimedia.org/wiki/File:Chin_ups_2.svg',
    ],
  },
  32: {
    frames: ['32-1.png', '32-2.png'],
    note: 'На рисунке узкий хват к груди — один из вариантов тяги верхнего блока. Положение корпуса и траектория те же.',
    sources: [
      'https://commons.wikimedia.org/wiki/File:Close-grip-front-lat-pull-down-1.png',
      'https://commons.wikimedia.org/wiki/File:Close-grip-front-lat-pull-down-2.png',
    ],
  },
  40: {
    frames: ['40-1.png', '40-2.png'],
    note: '',
    sources: [
      'https://commons.wikimedia.org/wiki/File:Squats-1.png',
      'https://commons.wikimedia.org/wiki/File:Squats-2.png',
    ],
  },
  41: {
    frames: ['41-1.png', '41-2.png'],
    note: '',
    sources: [
      'https://commons.wikimedia.org/wiki/File:Front_squat_with_barbell_1.svg',
      'https://commons.wikimedia.org/wiki/File:Front_squat_with_barbell_2.svg',
    ],
  },
  42: {
    frames: ['42-1.png', '42-2.png'],
    note: 'На рисунке узкая постановка стоп. Ширина — дело предпочтения, траектория одна.',
    sources: [
      'https://commons.wikimedia.org/wiki/File:Narrow-stance-leg-press-1-1024x671.png',
      'https://commons.wikimedia.org/wiki/File:Narrow-stance-leg-press-2-1024x671.png',
    ],
  },
  50: {
    frames: ['50-1.png', '50-2.png'],
    note: '',
    sources: [
      'https://commons.wikimedia.org/wiki/File:Barbell_dead_lifts_1.svg',
      'https://commons.wikimedia.org/wiki/File:Barbell_dead_lifts_2.svg',
    ],
  },
  51: {
    frames: ['51-1.png', '51-2.png'],
    note: '',
    sources: [
      'https://commons.wikimedia.org/wiki/File:Romanian_dead_lift_1.svg',
      'https://commons.wikimedia.org/wiki/File:Romanian_dead_lift_2.svg',
    ],
  },
  53: {
    frames: ['53-1.png', '53-2.png'],
    note: '',
    sources: [
      'https://commons.wikimedia.org/wiki/File:Hyperextensions_1.svg',
      'https://commons.wikimedia.org/wiki/File:Hyperextensions_2.svg',
    ],
  },
  60: {
    frames: ['60-1.png', '60-2.png'],
    note: '',
    sources: [
      'https://commons.wikimedia.org/wiki/File:Lateral_dumbbell_raises_1.svg',
      'https://commons.wikimedia.org/wiki/File:Lateral_dumbbell_raises_2.svg',
    ],
  },
  62: {
    frames: ['62-1.png', '62-2.png'],
    note: '',
    sources: [
      'https://commons.wikimedia.org/wiki/File:Biceps_curl_with_dumbbell_1.svg',
      'https://commons.wikimedia.org/wiki/File:Biceps_curl_with_dumbbell_2.svg',
    ],
  },
  63: {
    frames: ['63-1.png', '63-2.png'],
    note: '',
    sources: [
      'https://commons.wikimedia.org/wiki/File:Standing_biceps_curl_with_cable_1.svg',
      'https://commons.wikimedia.org/wiki/File:Standing_biceps_curl_with_cable_2.svg',
    ],
  },
  64: {
    frames: ['64-1.png', '64-2.png'],
    note: '',
    sources: [
      'https://commons.wikimedia.org/wiki/File:Triceps_pushdown_with_v_bar_and_cable_1.svg',
      'https://commons.wikimedia.org/wiki/File:Triceps_pushdown_with_v_bar_and_cable_2.svg',
    ],
  },
  65: {
    frames: ['65-1.png', '65-2.png'],
    note: '',
    sources: [
      'https://commons.wikimedia.org/wiki/File:Lying_triceps_extension_with_dumbbells_1.svg',
      'https://commons.wikimedia.org/wiki/File:Lying_triceps_extension_with_dumbbells_2.svg',
    ],
  },
  66: {
    frames: ['66-1.png', '66-2.png'],
    note: '',
    sources: [
      'https://commons.wikimedia.org/wiki/File:Butterfly_machine_1.svg',
      'https://commons.wikimedia.org/wiki/File:Butterfly_machine_2.svg',
    ],
  },
  67: {
    frames: ['67-1.png', '67-2.png'],
    note: '',
    sources: [
      'https://commons.wikimedia.org/wiki/File:Leg_extensions_1.svg',
      'https://commons.wikimedia.org/wiki/File:Leg_extensions_2.svg',
    ],
  },
  68: {
    frames: ['68-1.png', '68-2.png'],
    note: '',
    sources: [
      'https://commons.wikimedia.org/wiki/File:Lying_leg_curl_machine_1.svg',
      'https://commons.wikimedia.org/wiki/File:Lying_leg_curl_machine_2.svg',
    ],
  },
  69: {
    frames: ['69-1.png', '69-2.png'],
    note: '',
    sources: [
      'https://commons.wikimedia.org/wiki/File:Standing_calf_raises_using_machine_1.svg',
      'https://commons.wikimedia.org/wiki/File:Standing_calf_raises_using_machine_2.svg',
    ],
  },
  82: {
    frames: ['82-1.png', '82-2.png'],
    note: '',
    sources: [
      'https://commons.wikimedia.org/wiki/File:Seated_ab_crunch_with_cable_1.svg',
      'https://commons.wikimedia.org/wiki/File:Seated_ab_crunch_with_cable_2.svg',
    ],
  },
}
